import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../components/shared/useUser';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { client, MarketSupplier, Instance, Market } from '../../app/services/api/orderManagementClient';
import useLogError from '../../hooks/useLogError';

const SupplierForm = () => {
  const { t } = useTranslation('suppliers');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  
  const [formData, setFormData] = useState<MarketSupplier>({
    supplierId: 0,
    instanceName: '',
    companyId: 0,
    marketName: '',
    customerTypeId: 0,
    supplierNumber: '',
    supplierName: '',
    address1: '',
    address2: '',
    town: '',
    county: '',
    postcode: '',
    country: '',
    emailAddress: '',
    emailAddress2: '',
    fileFormat: '',
    publishMethod: '',
    supplierNumber2: '',
    cutOffTime: '',
    fileContents: 0,
    creditRequestCutOffTime: '',
    minimumOrderValue: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const fileFormatOptions = [
    { value: 'CSV', label: 'CSV' },
    { value: 'XLS', label: 'XLS' },
    { value: 'XLSX', label: 'XLSX' },
    { value: 'PDF', label: 'PDF' },
  ];

  const publishMethodOptions = [
    { value: 'EMAIL', label: 'EMAIL' },
    { value: 'FTP', label: 'FTP' },
    { value: 'FTP & EMAIL', label: 'FTP & EMAIL' },
  ];

  const fileContentsOptions = [
    { value: 0, label: t('consolidatedFile') },
    { value: 1, label: t('singleFile') },
  ];

  const fetchInstances = async () => {
    try {
      const result = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (result.success && result.data) {
        setInstances(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  };

  const fetchMarkets = async (companyId: number) => {
    try {
      const result = await client.getMarkets({
        companyId: companyId.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (result.success && result.data) {
        setMarkets(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching markets:', error);
      setMarkets([]);
    }
  };

  const fetchSupplier = async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const result = await client.getMarketSupplier(id);
      if (result.success && result.data) {
        setFormData(result.data);
        if (result.data.companyId) {
          fetchMarkets(result.data.companyId);
        }
      }
    } catch (error) {
      console.error('Error fetching supplier:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
    if (isEdit) {
      fetchSupplier();
    }
  }, [id, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.supplierName.trim()) {
      newErrors.supplierName = t('supplierNameRequired');
    }

    if (!formData.supplierNumber.trim()) {
      newErrors.supplierNumber = t('supplierNumberRequired');
    }

    if (!formData.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    if (!formData.customerTypeId) {
      newErrors.customerTypeId = t('marketRequired');
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = t('emailAddressRequired');
    }

    if (!formData.fileFormat) {
      newErrors.fileFormat = t('fileFormatRequired');
    }

    if (!formData.publishMethod) {
      newErrors.publishMethod = t('publishMethodRequired');
    }

    if (formData.minimumOrderValue !== undefined && formData.minimumOrderValue < 0) {
      newErrors.minimumOrderValue = t('minimumOrderValueMustBePositive');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketSupplier) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value: string | number = event.target.value;
    
    if (field === 'supplierId' || field === 'companyId' || field === 'customerTypeId' || field === 'fileContents' || field === 'minimumOrderValue') {
      value = value === '' ? 0 : parseInt(value) || 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      instanceName: value?.name || '',
      customerTypeId: 0, // Reset market when instance changes
      marketName: '',
    }));
    
    if (value?.id) {
      fetchMarkets(value.id);
    } else {
      setMarkets([]);
    }
    
    if (errors.companyId) {
      setErrors(prev => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    setFormData(prev => ({
      ...prev,
      customerTypeId: value?.customerTypeId || 0,
      marketName: value?.name || '',
    }));
    
    if (errors.customerTypeId) {
      setErrors(prev => ({
        ...prev,
        customerTypeId: '',
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateMarketSupplier(id!, formData);
        if (result.success) {
          setMessage(t('updateSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/suppliers${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      } else {
        const result = await client.createMarketSupplier(formData);
        if (result.success) {
          setMessage(t('createSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/suppliers${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit ? t('updateErrorMessage') : t('createErrorMessage')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/suppliers${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'SupplierUpdate' : 'SupplierCreate', 'ConfigurationAdmin']);

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{t(isEdit ? 'editSupplier' : 'createSupplier')}</PageTitle>
        <Typography>{t('loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack spacing={8}>
        <PageTitle>{t(isEdit ? 'editSupplier' : 'createSupplier')}</PageTitle>

        {/* Basic Supplier Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('basicInformation')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid container
              spacing={6}>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('supplierName')}
                  value={formData.supplierName}
                  onChange={handleInputChange('supplierName')}
                  error={!!errors.supplierName}
                  helperText={errors.supplierName}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('supplierNumber')}
                  value={formData.supplierNumber}
                  onChange={handleInputChange('supplierNumber')}
                  error={!!errors.supplierNumber}
                  helperText={errors.supplierNumber}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('supplierNumber2')}
                  value={formData.supplierNumber2}
                  onChange={handleInputChange('supplierNumber2')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <Autocomplete
                  options={instances}
                  getOptionLabel={(option) => option.name}
                  value={instances.find(instance => instance.id === formData.companyId) || null}
                  onChange={handleInstanceChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instance')}
                      error={!!errors.companyId}
                      helperText={errors.companyId}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <Autocomplete
                  options={markets}
                  getOptionLabel={(option) => option.name}
                  value={markets.find(market => market.customerTypeId === formData.customerTypeId) || null}
                  onChange={handleMarketChange}
                  disabled={!formData.companyId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('market')}
                      error={!!errors.customerTypeId}
                      helperText={errors.customerTypeId}
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Contact Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('contactInformation')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid container
              spacing={6}>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('emailAddress')}
                  type="email"
                  value={formData.emailAddress}
                  onChange={handleInputChange('emailAddress')}
                  error={!!errors.emailAddress}
                  helperText={errors.emailAddress}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('emailAddress2')}
                  type="email"
                  value={formData.emailAddress2}
                  onChange={handleInputChange('emailAddress2')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('address1')}
                  value={formData.address1}
                  onChange={handleInputChange('address1')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('address2')}
                  value={formData.address2}
                  onChange={handleInputChange('address2')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('town')}
                  value={formData.town}
                  onChange={handleInputChange('town')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('county')}
                  value={formData.county}
                  onChange={handleInputChange('county')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('postcode')}
                  value={formData.postcode}
                  onChange={handleInputChange('postcode')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('country')}
                  value={formData.country}
                  onChange={handleInputChange('country')}
                  fullWidth
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Order Processing Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('orderProcessingInformation')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid container
              spacing={6}>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('fileFormat')}
                  select
                  value={formData.fileFormat}
                  onChange={handleInputChange('fileFormat')}
                  error={!!errors.fileFormat}
                  helperText={errors.fileFormat}
                  fullWidth
                  required
                >
                  {fileFormatOptions.map((option) => (
                    <MenuItem key={option.value}
                      value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('publishMethod')}
                  select
                  value={formData.publishMethod}
                  onChange={handleInputChange('publishMethod')}
                  error={!!errors.publishMethod}
                  helperText={errors.publishMethod}
                  fullWidth
                  required
                >
                  {publishMethodOptions.map((option) => (
                    <MenuItem key={option.value}
                      value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('cutOffTime')}
                  type="time"
                  value={formData.cutOffTime || ''}
                  onChange={handleInputChange('cutOffTime')}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('creditRequestCutOffTime')}
                  type="time"
                  value={formData.creditRequestCutOffTime || ''}
                  onChange={handleInputChange('creditRequestCutOffTime')}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('fileContents')}
                  select
                  value={formData.fileContents !== undefined ? formData.fileContents : 0}
                  onChange={handleInputChange('fileContents')}
                  fullWidth
                >
                  {fileContentsOptions.map((option) => (
                    <MenuItem key={option.value}
                      value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('minimumOrderValue')}
                  type="number"
                  value={formData.minimumOrderValue || ''}
                  onChange={handleInputChange('minimumOrderValue')}
                  error={!!errors.minimumOrderValue}
                  helperText={errors.minimumOrderValue}
                  fullWidth
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons Card */}
        <Card
          elevation={0}
          sx={{ backgroundColor: 'background.default' }}>
          <CardContent sx={{ p: 8 }}>
            <Stack direction="row"
              spacing={3}
              justifyContent="flex-end">
              <Button
                variant="secondary"
                size="lg"
                onClick={handleCancel}>
                {t('cancel')}
              </Button>
              {userCanSave && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={isSaving}
                >
                  {isSaving ? t('saving') : (isEdit ? t('update') : t('create'))}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default SupplierForm;
